import React, {useState, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import i18n from 'i18next';

import styled from 'styled-components';

import {spacing} from 'styles/globals';
import Button from 'components/Button';
import Status from 'components/Status';

import FormField from 'components/FormField';
import TextInput from 'components/TextInput';
import TextArea from 'components/TextArea';

const ButtonDrawer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${spacing.xs};
`;

function doesFormHaveErrors(form) {
	return (
		form &&
		form.reportValidity &&
		form.checkValidity &&
		!form.checkValidity()
	);
}

function defaultCharCountRenderer(length, maxLength) {
	// When a negative value is returned from the character counter,
	// we assume invalid input that can't be counted.
	if (length < 0) {
		return i18n.t('error_invalid_formatting');
	}

	// Show the character counter when the length is within
	// 15 characters of the maximum  allowed length
	const charCountDisplayThreshold = maxLength - 15;

	if (length > charCountDisplayThreshold) {
		return i18n.t('actions_characters_used', {
			valueLength: length,
			maxLength,
		});
	}
}

function defaultCharCounter(value) {
	return value.trim().length;
}

function TextField(props) {
	const {
		description,
		descriptionType,
		id,
		initialValue = '',
		charCountRenderer = defaultCharCountRenderer,
		getCharCount = defaultCharCounter,
		maxLength = 64,
		multiline,
		onChange,
		placeholder,
		title,
		type,
		disabled,
		maxRows,
	} = props;

	const {t} = useTranslation();

	const formEl = useRef(null);
	const inputEl = useRef(null);
	const [value, setValue] = useState(initialValue);

	const isDirty = value.trim() !== initialValue;
	const showButtons = isDirty && !disabled;

	const inputLength = getCharCount(value);
	const canSubmitForm = inputLength >= 0 && inputLength <= maxLength;

	let descriptionText = description;
	if (typeof description === 'function') {
		descriptionText = description(value);
	}

	function handleChange(e) {
		setValue(e.target.value);
	}

	function submitValue(e) {
		e.stopPropagation();
		e.preventDefault();

		if (doesFormHaveErrors(formEl.current)) {
			formEl.current.reportValidity();
		} else if (onChange) {
			onChange(id, value.trim(), title);
			inputEl.current.focus();
		}
	}

	return (
		<form ref={formEl} onSubmit={submitValue}>
			<FormField
				id={id}
				label={title}
				description={descriptionText}
				descriptionType={descriptionType}
				shortHint={
					<Status>{charCountRenderer(inputLength, maxLength)}</Status>
				}
			>
				{multiline ? (
					<TextArea
						inputRef={inputEl}
						placeholder={placeholder}
						value={value}
						onChange={handleChange}
						disabled={disabled}
						maxRows={maxRows}
					/>
				) : (
					<TextInput
						inputRef={inputEl}
						type={type}
						placeholder={placeholder}
						value={value}
						onChange={handleChange}
						disabled={disabled}
					/>
				)}
			</FormField>
			{showButtons && (
				<ButtonDrawer>
					<Button
						size="medium"
						variant="transparent"
						onClick={() => setValue(initialValue)}
					>
						{t('actions_cancel')}
					</Button>
					<Button
						isDisabled={!canSubmitForm}
						type="submit"
						icon="ok"
						size="medium"
						variant="primary"
					>
						{t('actions_save')}
					</Button>
				</ButtonDrawer>
			)}
		</form>
	);
}

export default TextField;
